import React, { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";

export default function CombineScores({ userId }) {
  const isUuid = (value) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      String(value || "")
    );

  // ----------------------------------------------------------------
  // STATE MANAGEMENT
  // ----------------------------------------------------------------
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState("");

  // Position & Sport State
  const [availablePositions, setAvailablePositions] = useState([]);
  const [selectedPositionId, setSelectedPositionId] = useState("");
  const [primaryPositionId, setPrimaryPositionId] = useState("");
  const [secondaryPositionId, setSecondaryPositionId] = useState("");
  const [detectedSport, setDetectedSport] = useState("");

  // Score Data State
  const [skillScores, setSkillScores] = useState([]);
  const [combineScores, setCombineScores] = useState([]);
  const [userSkillScores, setUserSkillScores] = useState({});
  const [userCombineScores, setUserCombineScores] = useState({});

  // Loading state for save feedback
  const [isSaving, setIsSaving] = useState(false);

  // ----------------------------------------------------------------
  // 1. FETCH PROGRAMS
  // ----------------------------------------------------------------
  useEffect(() => {
    async function fetchPrograms() {
      const { data, error } = await supabase
        .from("user_program_follows")
        .select("program_id, programs(name, sport)")
        .eq("user_id", userId);
      if (error) console.error(error);
      else setPrograms(data || []);
    }
    fetchPrograms();
  }, [userId]);

  useEffect(() => {
    if (!programs.length) {
      if (selectedProgram) setSelectedProgram("");
      return;
    }

    const hasCurrent = programs.some((p) => p.program_id === selectedProgram);
    if (!hasCurrent) {
      setSelectedProgram(programs[0].program_id);
    }
  }, [programs, selectedProgram]);

  // ----------------------------------------------------------------
  // 2. DETECT SPORT, LOAD DROPDOWN OPTIONS, & SET DEFAULT POSITION
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!selectedProgram) {
      setDetectedSport("");
      setAvailablePositions([]);
      setSelectedPositionId("");
      return;
    }

    async function setupSportAndPosition() {
      try {
        const programObj = programs.find((p) => p.program_id === selectedProgram);
        const rawProgramSport = programObj?.programs?.sport;
        if (!rawProgramSport) return;

        const sportFromObject =
          rawProgramSport && typeof rawProgramSport === "object" ? rawProgramSport : null;

        let sportId = isUuid(rawProgramSport)
          ? rawProgramSport
          : sportFromObject?.id || "";
        let sportName =
          typeof rawProgramSport === "string" && !isUuid(rawProgramSport)
            ? rawProgramSport
            : sportFromObject?.name || "";

        if (!sportId && sportName) {
          const { data: sportLookup, error: sportError } = await supabase
            .from("development_sports")
            .select("id, name")
            .ilike("name", sportName.trim())
            .limit(1)
            .maybeSingle();

          if (!sportError && sportLookup?.id) {
            sportId = sportLookup.id;
            sportName = sportLookup.name || sportName;
          }
        }

        if (!sportId) {
          setAvailablePositions([]);
          setSelectedPositionId("");
          return;
        }

        setDetectedSport(sportName || "Unknown Sport");

        const [positionsResult, swotPositionsResult, userPositionResult] = await Promise.all([
          supabase
            .from("positions")
            .select("id, name")
            .eq("sport_id", sportId)
            .order("name", { ascending: true }),
          supabase
            .from("athlete_swot")
            .select("position_id, updated_at")
            .eq("athlete_id", userId)
            .eq("program_id", selectedProgram)
            .eq("status", "published")
            .not("position_id", "is", null)
            .order("updated_at", { ascending: false }),
          supabase
            .from("user_sport_info")
            .select("position, secondary_position")
            .eq("user_id", userId)
            .eq("sport", sportId)
            .maybeSingle()
        ]);

        const allSportPositions = positionsResult.data || [];
        const swotPositionIds = [];
        (swotPositionsResult.data || []).forEach((row) => {
          const posId = String(row.position_id || "").trim();
          if (!posId) return;
          if (!swotPositionIds.includes(posId)) swotPositionIds.push(posId);
        });
        setAvailablePositions(allSportPositions);

        if (swotPositionIds.length > 0) {
          const primaryPosition = swotPositionIds[0] || "";
          const secondaryPosition = swotPositionIds[1] || "";
          setPrimaryPositionId(primaryPosition);
          setSecondaryPositionId(secondaryPosition);
          setSelectedPositionId(primaryPosition || "");
          return;
        }

        // Fallback if no SWOT-assigned positions exist yet.
        const primaryPosition = userPositionResult.data?.position || "";
        const secondaryPosition = userPositionResult.data?.secondary_position || "";
        setPrimaryPositionId(primaryPosition || "");
        setSecondaryPositionId(secondaryPosition || "");
        setSelectedPositionId(primaryPosition || "");

      } catch (err) {
        console.error("Error setting up sport/position:", err);
      }
    }

    setupSportAndPosition();
  }, [selectedProgram, programs, userId]);

  // ----------------------------------------------------------------
  // 3. FETCH GOALS & SCORES
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!selectedProgram) return;

    async function fetchScores() {
      try {
        // --- Fetch Skill Metrics ---
        // Added drill_description and total_attempts to selection
        let skillQuery = supabase
          .from("program_goal_skill_scores")
          .select("id, skill_name, target_score, total_attempts, drill_description, position_ids, is_timed")
          .eq("program_id", selectedProgram);

        if (selectedPositionId) {
          skillQuery = skillQuery.overlaps("position_ids", [selectedPositionId]);
        }

        const { data: skillMetrics, error: skillError } = await skillQuery;
        if (skillError) throw skillError;
        setSkillScores(skillMetrics || []);

        // --- Fetch Combine Metrics ---
        // Use program_goal_combine_scores as canonical source.
        let combineMetrics = [];
        const { data: goalCombineMetrics, error: goalCombineError } = await supabase
          .from("program_goal_combine_scores")
          .select("id, drill_name, target_score, drill_description, is_timed")
          .eq("program_id", selectedProgram);
        if (goalCombineError) throw goalCombineError;
        combineMetrics = goalCombineMetrics || [];
        setCombineScores(combineMetrics);

        // --- Fetch User's Existing Scores ---
        if ((skillMetrics || []).length > 0) {
          let userSkillsQuery = supabase
            .from("user_skill_scores")
            .select("program_skill_score, user_score, position_id")
            .eq("athlete_id", userId)
            .eq("program_id", selectedProgram)
            .in("program_skill_score", skillMetrics.map((s) => s.id));
          if (selectedPositionId) {
            userSkillsQuery = userSkillsQuery.or(`position_id.eq.${selectedPositionId},position_id.is.null`);
          } else {
            userSkillsQuery = userSkillsQuery.is("position_id", null);
          }
          const { data: userSkills } = await userSkillsQuery;

          const skillMap = {};
          (userSkills || [])
            .sort((a, b) => (a.position_id ? 1 : 0) - (b.position_id ? 1 : 0))
            .forEach((s) => {
            skillMap[s.program_skill_score] = s.user_score;
            });
          setUserSkillScores(skillMap);
        }

        if ((combineMetrics || []).length > 0) {
          let userCombineQuery = supabase
            .from("user_combine_score")
            .select("program_combine_scores, user_score, position_id")
            .eq("athlete_id", userId)
            .eq("program_id", selectedProgram);
          if (selectedPositionId) {
            userCombineQuery = userCombineQuery.or(`position_id.eq.${selectedPositionId},position_id.is.null`);
          } else {
            userCombineQuery = userCombineQuery.is("position_id", null);
          }
          const { data: userCombines, error: userCombinesError } = await userCombineQuery;
          if (userCombinesError) throw userCombinesError;

          const metricIds = new Set(combineMetrics.map((c) => String(c.id)));
          const combineMap = {};
          (userCombines || [])
            .sort((a, b) => (a.position_id ? 1 : 0) - (b.position_id ? 1 : 0))
            .forEach((c) => {
            const resolvedId = c.program_combine_scores;
            if (!resolvedId) return;
            if (!metricIds.has(String(resolvedId))) return;
            combineMap[resolvedId] = c.user_score;
            });
          setUserCombineScores(combineMap);
        }
      } catch (error) {
        console.error("Error fetching scores:", error);
      }
    }

    fetchScores();
  }, [selectedProgram, selectedPositionId, userId]);

  // ----------------------------------------------------------------
  // HANDLERS
  // ----------------------------------------------------------------
  const isValidTimedScoreInput = (value) => value === "" || /^\d+(\.\d{0,1})?$/.test(value);

  const handleSkillChange = (metricId, value, isTimed) => {
    if (isTimed && !isValidTimedScoreInput(value)) return;
    setUserSkillScores({ ...userSkillScores, [metricId]: value });
  };

  const handleCombineChange = (metricId, value, isTimed) => {
    if (isTimed && !isValidTimedScoreInput(value)) return;
    setUserCombineScores({ ...userCombineScores, [metricId]: value });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      for (const [metricId, score] of Object.entries(userSkillScores)) {
        const normalizedSkillScore = score === "" ? null : Number(score);
        if (normalizedSkillScore === null || Number.isNaN(normalizedSkillScore)) continue;

        let updateSkillQuery = supabase
          .from("user_skill_scores")
          .update({ user_score: normalizedSkillScore })
          .eq("athlete_id", userId)
          .eq("program_id", selectedProgram)
          .eq("program_skill_score", metricId);
        updateSkillQuery = selectedPositionId
          ? updateSkillQuery.eq("position_id", selectedPositionId)
          : updateSkillQuery.is("position_id", null);
        const { data: updatedRows, error: updateError } = await updateSkillQuery.select("id");
        if (updateError) throw updateError;

        if (!updatedRows || updatedRows.length === 0) {
          const { error: insertError } = await supabase
            .from("user_skill_scores")
            .insert({
              athlete_id: userId,
              program_id: selectedProgram,
              program_skill_score: metricId,
              position_id: selectedPositionId || null,
              user_score: normalizedSkillScore
            });
          if (insertError) throw insertError;
        }
      }
      for (const [metricId, score] of Object.entries(userCombineScores)) {
        const normalizedCombineScore = score === "" ? null : Number(score);
        if (normalizedCombineScore === null || Number.isNaN(normalizedCombineScore)) continue;

        let updateCombineQuery = supabase
          .from("user_combine_score")
          .update({ user_score: normalizedCombineScore, updated_at: new Date().toISOString() })
          .eq("athlete_id", userId)
          .eq("program_id", selectedProgram)
          .eq("program_combine_scores", metricId);
        updateCombineQuery = selectedPositionId
          ? updateCombineQuery.eq("position_id", selectedPositionId)
          : updateCombineQuery.is("position_id", null);
        const { data: updatedRows, error: updateError } = await updateCombineQuery.select("id");
        if (updateError) throw updateError;

        if (!updatedRows || updatedRows.length === 0) {
          const { error: insertError } = await supabase
            .from("user_combine_score")
            .insert({
              athlete_id: userId,
              program_id: selectedProgram,
              program_combine_scores: metricId,
              position_id: selectedPositionId || null,
              user_score: normalizedCombineScore
            });
          if (insertError) throw insertError;
        }
      }
      alert("Scores saved successfully!");
    } catch (e) {
      console.error(e);
      alert("Error saving scores.");
    } finally {
      setIsSaving(false);
    }
  };

  // Helper to determine status color (User vs Target)
  const getStatusColor = (userScore, targetScore, isTimed) => {
    if (!userScore) return "border-gray-200 focus:ring-blue-500 text-gray-900"; // Default
    const uScore = Number(userScore);
    const tScore = Number(targetScore);

    // Logic: If timed, lower is better. If score, higher is better.
    const isSuccess = isTimed ? uScore <= tScore : uScore >= tScore;

    return isSuccess
      ? "border-green-500 bg-green-50 text-green-700 focus:ring-green-500"
      : "border-red-300 bg-white text-red-600 focus:ring-red-500";
  };

  // Components shared with Admin UI
  const StopwatchBadge = () => (
    <div className="flex items-center gap-1 bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full border border-yellow-200 mb-2 w-fit" title="This drill is timed">
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
      <span className="text-[10px] font-bold uppercase tracking-wide">Timed</span>
    </div>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto font-sans">
      <div className="flex justify-between items-end mb-6">
        <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">Athlete Combine</h1>
        {(skillScores.length > 0 || combineScores.length > 0) && (
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-gray-900 hover:bg-gray-800 text-white font-semibold px-6 py-2.5 rounded-lg shadow-lg transition-all active:scale-95 disabled:opacity-50"
          >
            {isSaving ? "Saving..." : "Save All Scores"}
          </button>
        )}
      </div>

      {/* -------------------- FILTERS -------------------- */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block mb-2 font-semibold text-gray-700">Select Program:</label>
          <select
            className="border border-gray-300 p-3 rounded-lg w-full bg-white shadow-sm focus:ring-2 focus:ring-blue-500 outline-none transition"
            value={selectedProgram || ""}
            onChange={(e) => setSelectedProgram(e.target.value)}
          >
            <option value="" disabled>-- Choose Program --</option>
            {programs.map((p) => (
              <option key={p.program_id} value={p.program_id}>{p.programs.name}</option>
            ))}
          </select>
          {detectedSport && (
            <p className="text-xs text-gray-500 mt-2 font-medium">Sport: {detectedSport}</p>
          )}
        </div>

        {selectedProgram && (
          <div>
            <label className="block mb-2 font-semibold text-gray-700">Target Position:</label>
            <select
              className={`border p-3 rounded-lg w-full shadow-sm outline-none transition ${selectedPositionId ? "bg-blue-50 border-blue-200 text-blue-800 font-medium" : "bg-white border-gray-300 text-gray-500"}`}
              value={selectedPositionId || ""}
              onChange={(e) => setSelectedPositionId(e.target.value)}
            >
              <option value="">-- View All Positions --</option>
              {availablePositions.map((pos) => (
                <option key={pos.id} value={pos.id}>
                  {pos.name}
                  {String(pos.id) === String(primaryPositionId) ? " (Primary)" : ""}
                  {String(pos.id) === String(secondaryPositionId) ? " (Secondary)" : ""}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* -------------------- CONTENT SECTIONS -------------------- */}

      {/* 1. Skill Scores */}
      {/* 1. Skill Scores */}
      {skillScores.length > 0 && (
        <div className="mb-10">
          <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-2">Skill Drills</h2>
          {/* Responsive grid: 1 col on mobile, 2 on tablet, 3 on desktop */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {skillScores.map((metric) => (
              <div key={metric.id} className="relative p-6 rounded-xl shadow-sm border border-gray-200 bg-white hover:shadow-xl transition-all duration-200 flex flex-col h-full">

                {/* Header Area */}
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-bold text-lg text-gray-900 leading-tight mr-2">{metric.skill_name}</h3>
                  {metric.is_timed && <StopwatchBadge />}
                </div>

                {/* Description - flex-grow ensures the input area aligns at the bottom across the row */}
                <p className="text-sm text-gray-500 mb-6 flex-grow">
                  {metric.drill_description || "No description provided."}
                </p>

                {/* Input Area */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                  <div className="flex items-center justify-between gap-4">

                    {/* Target Info */}
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 text-left">Target</span>
                      <div className="flex items-baseline gap-1">
                        <span className="text-xl font-black text-gray-800">{metric.target_score}</span>
                        {metric.total_attempts > 0 && (
                          <span className="text-xs font-semibold text-gray-400">/ {metric.total_attempts}</span>
                        )}
                      </div>
                    </div>

                    {/* User Input */}
                    <div className="flex-1 text-right">
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 text-right">Your Score</label>
                      <input
                        type="number"
                        step={metric.is_timed ? "0.1" : "1"}
                        inputMode={metric.is_timed ? "decimal" : "numeric"}
                        className={`w-24 text-center p-2 rounded-lg border-2 text-lg font-bold outline-none transition-all ${getStatusColor(userSkillScores[metric.id], metric.target_score, metric.is_timed)}`}
                        placeholder="-"
                        value={userSkillScores[metric.id] || ""}
                        onChange={(e) => handleSkillChange(metric.id, e.target.value, metric.is_timed)}
                      />
                    </div>
                  </div>
                </div>
                {/* Footer removed for a cleaner look */}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 2. Combine Scores */}
      {combineScores.length > 0 && (
        <div className="mb-10">
          <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-2">Combine Drills</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {combineScores.map((metric) => (
              <div key={metric.id} className="relative p-6 rounded-xl shadow-sm border border-gray-200 bg-white hover:shadow-xl transition-all duration-200 flex flex-col h-full">

                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-bold text-lg text-gray-900 leading-tight mr-2">{metric.drill_name}</h3>
                  {metric.is_timed && <StopwatchBadge />}
                </div>

                <p className="text-sm text-gray-500 mb-6 flex-grow">
                  {metric.drill_description || "No description provided."}
                </p>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                  <div className="flex items-center justify-between gap-4">

                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Target</span>
                      <div className="flex items-baseline gap-1">
                        <span className="text-xl font-black text-gray-800">{metric.target_score}</span>
                        {metric.total_attempts > 0 && (
                          <span className="text-xs font-semibold text-gray-400">/ {metric.total_attempts}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex-1 text-right">
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 text-right">Your Score</label>
                      <input
                        type="number"
                        step={metric.is_timed ? "0.1" : "1"}
                        inputMode={metric.is_timed ? "decimal" : "numeric"}
                        className={`w-24 text-center p-2 rounded-lg border-2 text-lg font-bold outline-none transition-all ${getStatusColor(userCombineScores[metric.id], metric.target_score, metric.is_timed)}`}
                        placeholder="-"
                        value={userCombineScores[metric.id] || ""}
                        onChange={(e) => handleCombineChange(metric.id, e.target.value, metric.is_timed)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {selectedProgram && skillScores.length === 0 && combineScores.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
          <p className="text-gray-500 font-medium">No drills found for this program/position.</p>
        </div>
      )}
    </div>
  );
}
